import { useEffect, useMemo, useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import type { ScheduleInstallment } from "@/lib/financiacion/computeMetrics";

interface Props {
  schedule: ScheduleInstallment[];
  isLoan: boolean;
  /** Si se pasa, muestra un formulario para añadir cuotas al cuadro. */
  onAdd?: (row: ScheduleInstallment) => Promise<boolean>;
}

const parseNum = (v: string): number => {
  const n = Number((v || "").trim().replace(/\./g, "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

const fmt = (n: number) =>
  new Intl.NumberFormat("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const NUM_COLS = ["principal", "interest", "insurance", "total"] as const;
type NumCol = (typeof NUM_COLS)[number];

const ScheduleTable = ({ schedule, isLoan, onAdd }: Props) => {
  const [sel, setSel] = useState<{ aRow: number; aCol: number; fRow: number; fCol: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const [popPos, setPopPos] = useState<{ x: number; y: number } | null>(null);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ number: "", date: "", principal: "", interest: "", insurance: "", total: "", pending: "" });

  const startAdd = () => {
    const nextN = schedule.reduce((m, s) => Math.max(m, s.installment_number), 0) + 1;
    setForm({ number: String(nextN), date: "", principal: "", interest: "", insurance: "", total: "", pending: "" });
    setAdding(true);
  };

  const submitAdd = async () => {
    if (!onAdd) return;
    const n = parseInt(form.number, 10);
    if (!n || !form.date) return;
    const row: ScheduleInstallment = {
      installment_number: n,
      due_date: form.date,
      principal: parseNum(form.principal),
      interest: parseNum(form.interest),
      insurance: parseNum(form.insurance),
      total: parseNum(form.total),
      pending_capital: form.pending.trim() ? parseNum(form.pending) : null,
    };
    setSaving(true);
    const ok = await onAdd(row);
    setSaving(false);
    if (ok) setAdding(false);
  };

  useEffect(() => {
    const up = () => setDragging(false);
    window.addEventListener("mouseup", up);
    return () => window.removeEventListener("mouseup", up);
  }, []);

  const cellValue = (r: number, c: number): number => {
    const row = schedule[r];
    if (!row) return 0;
    const key: NumCol = NUM_COLS[c];
    return Number(row[key]) || 0;
  };

  const inRange = (r: number, c: number) => {
    if (!sel) return false;
    const [r1, r2] = [Math.min(sel.aRow, sel.fRow), Math.max(sel.aRow, sel.fRow)];
    const [c1, c2] = [Math.min(sel.aCol, sel.fCol), Math.max(sel.aCol, sel.fCol)];
    return r >= r1 && r <= r2 && c >= c1 && c <= c2;
  };

  const cellProps = (r: number, c: number, extra = "") => ({
    onMouseDown: (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      e.preventDefault();
      setDragging(true);
      setPopPos({ x: e.clientX, y: e.clientY });
      if (e.shiftKey && sel) setSel({ ...sel, fRow: r, fCol: c });
      else setSel({ aRow: r, aCol: c, fRow: r, fCol: c });
    },
    onMouseEnter: (e: React.MouseEvent) => {
      if (dragging && sel) {
        setSel({ ...sel, fRow: r, fCol: c });
        setPopPos({ x: e.clientX, y: e.clientY });
      }
    },
    className: `text-xs text-right tabular-nums cursor-cell select-none ${extra} ${
      inRange(r, c) ? "bg-primary/20 ring-1 ring-inset ring-primary/60" : ""
    }`,
  });

  const total = useMemo(() => {
    if (!sel) return null;
    const [r1, r2] = [Math.min(sel.aRow, sel.fRow), Math.max(sel.aRow, sel.fRow)];
    const [c1, c2] = [Math.min(sel.aCol, sel.fCol), Math.max(sel.aCol, sel.fCol)];
    let sum = 0;
    for (let r = r1; r <= r2; r++) for (let c = c1; c <= c2; c++) sum += cellValue(r, c);
    return sum;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel, schedule]);

  if (schedule.length === 0) {
    return <p className="text-xs text-muted-foreground text-center py-4">Sin cuadro de amortización.</p>;
  }

  const today = new Date().toISOString().slice(0, 10);

  // Capital pendiente: usa el valor de la fuente si existe; si no, calcula
  // saldo restante = suma de principales pendientes tras cada cuota.
  const totalPrincipal = schedule.reduce((s, r) => s + (Number(r.principal) || 0), 0);
  let runningPaid = 0;
  const derivedPending: number[] = schedule.map((r) => {
    runningPaid += Number(r.principal) || 0;
    return Math.max(0, totalPrincipal - runningPaid);
  });

  return (
    <div className="space-y-2">
      <div className="rounded border bg-background overflow-x-auto" onMouseLeave={() => setDragging(false)}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs w-16">Cuota</TableHead>
              <TableHead className="text-xs w-28">Fecha Vto.</TableHead>
              <TableHead className="text-xs text-right w-32">{isLoan ? "Amort. capital" : "Recup. coste"}</TableHead>
              <TableHead className="text-xs text-right w-32">{isLoan ? "Intereses" : "Carga financiera"}</TableHead>
              <TableHead className="text-xs text-right w-24">Seguro</TableHead>
              <TableHead className="text-xs text-right w-28">Total</TableHead>
              <TableHead className="text-xs text-right w-32">Capital pdte.</TableHead>
              <TableHead className="text-xs w-24">Estado</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {schedule.map((s, r) => {
              const paid = s.due_date < today;
              const pend = s.pending_capital != null ? s.pending_capital : derivedPending[r];
              const isDerived = s.pending_capital == null;
              return (
                <TableRow key={s.installment_number} className={paid ? "opacity-60" : ""}>
                  <TableCell className="text-xs font-mono">{s.installment_number}</TableCell>
                  <TableCell className="text-xs">{s.due_date}</TableCell>
                  <TableCell {...cellProps(r, 0)}>{fmt(s.principal)}</TableCell>
                  <TableCell {...cellProps(r, 1)}>{fmt(s.interest)}</TableCell>
                  <TableCell {...cellProps(r, 2)}>{fmt(s.insurance)}</TableCell>
                  <TableCell {...cellProps(r, 3, "font-medium")}>{fmt(s.total)}</TableCell>
                  <TableCell
                    className={`text-xs text-right tabular-nums ${isDerived ? "text-muted-foreground italic" : "font-medium"}`}
                    title={isDerived ? "Calculado como saldo restante (no importado de la fuente)" : "Importado del cuadro original"}
                  >
                    {fmt(pend)}
                  </TableCell>
                  <TableCell className="text-xs">
                    {paid ? (
                      <span className="text-muted-foreground">Pagada</span>
                    ) : (
                      <span className="text-emerald-600 dark:text-emerald-400">Pendiente</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {onAdd && !adding && (
        <Button type="button" variant="outline" size="sm" className="gap-1.5 text-xs" onClick={startAdd}>
          <Plus className="h-3.5 w-3.5" />
          Añadir cuota
        </Button>
      )}

      {onAdd && adding && (
        <div className="rounded border bg-muted/30 p-3 space-y-2">
          <p className="text-xs font-medium">Nueva cuota</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
            <label className="text-[11px] text-muted-foreground space-y-0.5">
              Nº cuota
              <input
                type="number"
                className="w-full h-8 rounded border bg-background px-2 text-xs"
                value={form.number}
                onChange={(e) => setForm({ ...form, number: e.target.value })}
              />
            </label>
            <label className="text-[11px] text-muted-foreground space-y-0.5">
              Fecha vto.
              <input
                type="date"
                className="w-full h-8 rounded border bg-background px-2 text-xs"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
              />
            </label>
            <label className="text-[11px] text-muted-foreground space-y-0.5">
              {isLoan ? "Amort. capital" : "Recup. coste"}
              <input
                type="text"
                inputMode="decimal"
                className="w-full h-8 rounded border bg-background px-2 text-xs text-right"
                value={form.principal}
                onChange={(e) => setForm({ ...form, principal: e.target.value })}
              />
            </label>
            <label className="text-[11px] text-muted-foreground space-y-0.5">
              {isLoan ? "Intereses" : "Carga financiera"}
              <input
                type="text"
                inputMode="decimal"
                className="w-full h-8 rounded border bg-background px-2 text-xs text-right"
                value={form.interest}
                onChange={(e) => setForm({ ...form, interest: e.target.value })}
              />
            </label>
            <label className="text-[11px] text-muted-foreground space-y-0.5">
              Seguro
              <input
                type="text"
                inputMode="decimal"
                className="w-full h-8 rounded border bg-background px-2 text-xs text-right"
                value={form.insurance}
                onChange={(e) => setForm({ ...form, insurance: e.target.value })}
              />
            </label>
            <label className="text-[11px] text-muted-foreground space-y-0.5">
              Total
              <input
                type="text"
                inputMode="decimal"
                className="w-full h-8 rounded border bg-background px-2 text-xs text-right"
                value={form.total}
                onChange={(e) => setForm({ ...form, total: e.target.value })}
              />
            </label>
            <label className="text-[11px] text-muted-foreground space-y-0.5">
              Capital pdte. (opc.)
              <input
                type="text"
                inputMode="decimal"
                className="w-full h-8 rounded border bg-background px-2 text-xs text-right"
                value={form.pending}
                onChange={(e) => setForm({ ...form, pending: e.target.value })}
              />
            </label>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              className="text-xs"
              disabled={saving || !form.number || !form.date}
              onClick={submitAdd}
            >
              {saving ? "Guardando…" : "Guardar cuota"}
            </Button>
            <Button type="button" variant="ghost" size="sm" className="text-xs" onClick={() => setAdding(false)}>
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {total !== null && popPos && (
        <div
          className="fixed z-50 text-xs bg-popover text-popover-foreground border rounded-md shadow-lg px-3 py-2"
          style={{
            left: Math.min(popPos.x + 14, (typeof window !== "undefined" ? window.innerWidth : 1200) - 180),
            top: Math.min(popPos.y + 14, (typeof window !== "undefined" ? window.innerHeight : 800) - 40),
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <span className="font-medium">Total</span>{" "}
          <span className="font-mono tabular-nums font-semibold">{fmt(total)}</span>
        </div>
      )}
    </div>
  );
};

export default ScheduleTable;
